import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, Card, Form, Message, Header, Segment, Loader, Select } from 'semantic-ui-react';

const SuperAdminWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const TestEmailForm = styled(Form)`
  margin-bottom: 20px;
`;

const PreviewCard = styled(Card)`
  width: 100% !important;
  margin-bottom: 20px;
`;

const SuperAdmin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<{ html: string; subject: string } | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; content: string } | null>(null);
  const [email, setEmail] = useState('jonas.b.frid@gmail.com');
  const [templateType, setTemplateType] = useState('weekly-digest');

  const userProfile = useSelector((state: any) => state.user.userProfile);
  const idToken = useSelector((state: any) => state.user.idToken);

  const handlePreview = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/global/test-email-preview?template=${templateType}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setPreview(data);
    } catch (error) {
      console.error('Preview failed:', error);
      setMessage({
        type: 'error',
        content: `Failed to generate preview: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/global/send-test-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            templateType
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      await response.json();
      setMessage({
        type: 'success',
        content: `Test email sent successfully to ${email}!`
      });
    } catch (error) {
      console.error('Send test email failed:', error);
      setMessage({
        type: 'error',
        content: `Failed to send test email: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  if (!userProfile) {
    return <Loader active>Loading user profile...</Loader>;
  }

  const templateOptions = [
    { key: 'weekly-digest', value: 'weekly-digest', text: 'Weekly Digest' },
    { key: 'daily-digest', value: 'daily-digest', text: 'Daily Digest' },
  ];

  return (
    <SuperAdminWrapper>
      <Header as="h1" color="blue">
        📧 Email Testing Interface
      </Header>

      <Message info>
        <Message.Header>Email Template Testing</Message.Header>
        <p>Use this interface to preview and send test emails with the latest templates.</p>
      </Message>

      {message && (
        <Message positive={message.type === 'success'} negative={message.type === 'error'}>
          {message.content}
        </Message>
      )}

      <Segment>
        <Header as="h3">Test Email Configuration</Header>

        <TestEmailForm>
          <Form.Field>
            <label>Template Type</label>
            <Select
              placeholder="Select template type"
              options={templateOptions}
              value={templateType}
              onChange={(e, { value }) => setTemplateType(value as string)}
            />
          </Form.Field>

          <Form.Field>
            <label>Test Email Address</label>
            <Form.Input
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Form.Field>

          <Form.Group>
            <Button
              type="button"
              onClick={handlePreview}
              loading={loading}
              disabled={!templateType}
            >
              Generate Preview
            </Button>
            <Button
              type="button"
              primary
              onClick={handleSendTestEmail}
              loading={loading}
              disabled={!email || !templateType}
            >
              Send Test Email
            </Button>
          </Form.Group>
        </TestEmailForm>
      </Segment>

      {preview && (
        <PreviewCard>
          <Card.Content>
            <Card.Header>Email Preview: {preview.subject}</Card.Header>
            <Card.Description>
              {/*
                Intentionally NOT run through the sanitizeHtml allowlist: this is
                a server-generated email-template preview (GET /global/test-email-preview),
                not user-controlled content, and it legitimately relies on <style>,
                <table> and inline styles that the strict rich-text allowlist would
                strip — breaking the preview. Rendered only inside the super-admin
                route to a super-admin. Source is trusted (our own templates).
              */}
              <div
                dangerouslySetInnerHTML={{ __html: preview.html }}
                style={{
                  border: '1px solid #ddd',
                  padding: '10px',
                  maxHeight: '400px',
                  overflow: 'auto',
                  backgroundColor: '#f9f9f9'
                }}
              />
            </Card.Description>
          </Card.Content>
        </PreviewCard>
      )}
    </SuperAdminWrapper>
  );
};

export default SuperAdmin;
